import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@jsverse/transloco';

import { AccessArea, AREA_META, AreaState } from '../../models/access-control.model';
import { AccessMode } from '../../models/entitlement.model';

/**
 * One access-area row in the member-access screen: the area's name, what it
 * covers, and a segmented control for its state.
 *
 * Deliberately the same visual language as `app-entitlement-overrides-panel`
 * — an Off / View only / Full access segmented control per named row —
 * because the entitlements tab is the vocabulary the operator already has,
 * and the backend modelled `AreaAccessMode` on that control for the same
 * reason.
 *
 * ## Two or three states, decided by the server
 *
 * `supportsRead` comes from the server's `read_only_areas` and must never be
 * hard-coded — not even as a count, since the area list is not fixed (T7 split
 * the compliance bundle into Frameworks / Controls / Tasks). Most grantable
 * areas offer a view-only state; Trust Centre, Organisation settings and the
 * billing-state bypass have no read-only form (asking for `read` on those is a
 * 422, not a silent promotion). So this control renders three buttons for some
 * rows and two for others.
 *
 * ## Areas that are inert on a grant
 *
 * `AREA_META[area].grantNote` marks an area whose capability resolves but
 * whose real gate refuses a delegate anyway — Trust Centre needs a *direct*
 * admin of the target, and the AI-import flow refuses delegated callers on
 * the scope axis. Pass `showGrantNote` on the grant panel to surface it.
 * Left unlabelled, each of these gets reported as a bug.
 *
 * Purely presentational — it holds no state and performs no writes. The
 * parent owns the pending state and the save.
 */
@Component({
  selector: 'app-area-access-control',
  templateUrl: './area-access-control.html',
  styleUrl: './area-access-control.css',
  imports: [MatIconModule, MatTooltipModule, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AreaAccessControlComponent {
  readonly area = input.required<AccessArea>();
  /** Current state. `'off'` plus the two `AccessMode` values. */
  readonly state = input.required<AreaState>();
  /**
   * Whether this area offers a view-only state — from the server's
   * `read_only_areas`. False renders a two-button control.
   */
  readonly supportsRead = input<boolean>(false);
  /**
   * Whether this org's plan permits Full access on this area — from the
   * server's `entitled_area_modes[area] === 'read_write'`.
   *
   * Disables the Full access button rather than hiding it: a row that loses a
   * button reads as "this area has no full state", which is what
   * {@link supportsRead} means for the middle one. This is the other axis —
   * the state exists and the plan does not reach it — so it stays visible with
   * a reason attached.
   *
   * Defaults true so the grant panel, whose targets are other orgs with their
   * own plans, is unaffected.
   */
  readonly supportsWrite = input<boolean>(true);
  readonly disabled = input<boolean>(false);
  /**
   * Surface `AREA_META[area].grantNote` when set. True on the grant
   * (Restricted) panel, false on the member panel — the notes describe
   * behaviour specific to delegated access.
   */
  readonly showGrantNote = input<boolean>(false);

  readonly stateChange = output<AreaState>();

  /** The two on-states, named so the template does not import the enum. */
  protected readonly readMode = AccessMode.READ;
  protected readonly readWriteMode = AccessMode.READ_WRITE;

  protected readonly meta = computed(() => AREA_META[this.area()]);

  /** Only shown when the caller asked for it *and* this area has one. */
  protected readonly grantNote = computed(() =>
    this.showGrantNote() ? this.meta().grantNote : undefined
  );

  protected select(next: AreaState): void {
    if (this.disabled() || next === this.state()) return;
    // Belt-and-braces against a caller that styles the button enabled: the
    // save would be refused by the entitlement gate anyway, and emitting
    // would mark the row dirty with a state it cannot keep.
    if (next === this.readWriteMode && !this.supportsWrite()) return;
    this.stateChange.emit(next);
  }
}
